import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { TableCategoryService } from '../services/api/table-category/table-category.service';
import { UserAdminMenuPermission } from '../interfaces/user.interface';
import { CurrentUserService } from '../services/Current-user/currentuser.service';

declare var _paq: any[];

// import { AuthService } from '../services/auth.service';
// import { UserSetting } from '../settings/user-setting';
// import { TableCategoryService } from '../services/table-category.service';
// import { UserAdminMenuPermission } from '../interfaces/users.interface';

@Injectable()
export class InitService {
  public userData: any;
  public userAccessToken: any;
  constructor(
    private oidcSecurityService: OidcSecurityService,
    // private readonly authService: AuthService,
    private tablecategoryservice: TableCategoryService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private currentUserService: CurrentUserService,
  ) {}

  private static readonly CALLBACK_RETRY_KEY = 'oidc_callback_retry';

  private static readonly CALLBACK_PARAMS = [
    'code',
    'state',
    'session_state',
    'scope',
    'iss',
    'id_token',
    'token',
    'error',
    'error_description',
  ];

  checkToken(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const cameBackFromIdentityServer = this.isCallbackUrl();

      this.oidcSecurityService.checkAuth().subscribe({
        next: (r: any) => {
          try {
            if (!r.accessToken) {
              const url: string = window.location.pathname;
              const params: string = window.location.search;
              sessionStorage.setItem('urls', url);
              sessionStorage.setItem('params', params.slice(1, params.length));

              this.oidcSecurityService.authorize();

              return;
            } else {
              sessionStorage.removeItem(InitService.CALLBACK_RETRY_KEY);
              this.userData = r.userData;
              this.userAccessToken = r.accessToken;
              resolve(true);
            }
          } catch (e) {
            console.error('Auth initialisation failed', e);
            resolve(false);
          }
        },
        error: (e) => {
          console.error('checkAuth() failed', e);
          this.recoverFromFailedCallback(cameBackFromIdentityServer, resolve);
        },
      });
    });
  }

  private isCallbackUrl(): boolean {
    const params = new URLSearchParams(window.location.search);

    return params.has('code') || params.has('error');
  }

  private recoverFromFailedCallback(
    cameBackFromIdentityServer: boolean,
    resolve: (authenticated: boolean) => void,
  ): void {
    if (!cameBackFromIdentityServer) {
      resolve(false);

      return;
    }

    this.stripCallbackParamsFromUrl();

    const attempts = Number(sessionStorage.getItem(InitService.CALLBACK_RETRY_KEY) ?? '0');

    if (attempts >= 1) {
      sessionStorage.removeItem(InitService.CALLBACK_RETRY_KEY);
      resolve(false);

      return;
    }

    sessionStorage.setItem(InitService.CALLBACK_RETRY_KEY, String(attempts + 1));
    this.oidcSecurityService.authorize();
  }

  private stripCallbackParamsFromUrl(): void {
    try {
      const url = new URL(window.location.href);

      InitService.CALLBACK_PARAMS.forEach((param) => url.searchParams.delete(param));

      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }

  menuPermissionFixer(menuPermission: UserAdminMenuPermission[]): {
    [key: string]: boolean;
  } {
    let final = {};

    menuPermission?.forEach((m) => {
      Object.assign(final, { [m.name]: m.isGranted });
    });
    return final;
  }

  private toBooleanLike(value: unknown): boolean {
    if (value === true || value === 1 || value === '1') return true;
    if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
    return false;
  }

  setUserInfoN(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.currentUserService.user.id = this.userData.sub;
      this.currentUserService.user.username = this.userData.preferred_username;
      this.currentUserService.user.token = this.userAccessToken;

      // Tell Matomo which user is logged in
      if (typeof _paq !== 'undefined') {
        _paq.push(['setUserId', this.userData?.preferred_username || this.userData?.sub]);
      }

      this.tablecategoryservice.menu(this.userData.sub).subscribe({
        next: (r: any) => {
          const u = sessionStorage.getItem('urls');
          const p = sessionStorage.getItem('params') ?? '';

          if (u) {
            sessionStorage.removeItem('urls');
            sessionStorage.removeItem('params');
            if (u == '/') this.router.navigate(['']);
            else
              this.router.navigate([u], {
                queryParams: Object.fromEntries(
                  Object.entries(this.parseQueryString(p)).map(([key, value]) => [
                    key,
                    typeof value === 'object' ? JSON.stringify(value) : value,
                  ]),
                ),
              });
          }

          this.currentUserService.user.menu = this.makeMenuTree(r.tablePermissions.items);
          this.currentUserService.user.adminMenu = this.menuPermissionFixer(r.menuPermission);
          this.currentUserService.user.isAdmin = this.toBooleanLike(
            r.isAdmin ?? r.isadmin ?? this.userData?.isAdmin ?? this.userData?.isadmin,
          );
          this.currentUserService.user.isApi = r.isApi;

          // Extract and store allowed table IDs
          this.extractTableIds(r.tablePermissions.items);

          this.currentUserService.markMenuReady();
          resolve(true);
        },

        error: (e) => {
          this.currentUserService.markMenuReady();
          resolve(false);
        },
      });
    });
  }
  makeMenuTree(data: any[]): any[] {
    const nodeMap: { [id: string]: any } = {};

    data.forEach((node) => {
      nodeMap[node.id] = { ...node, children: [] };
    });

    const rootNodes: any[] = [];
    data.forEach((node) => {
      if (node.parentId) {
        const parentNode = nodeMap[node.parentId];
        if (parentNode) {
          parentNode.children.push(nodeMap[node.id]);
          parentNode.collapsable = true;
        }
      } else {
        node.isTitle = true;
        rootNodes.push(nodeMap[node.id]);
      }
    });

    return this.removeTableLess(rootNodes);
  }

  removeTableLess(node: any[]) {
    return node.filter((item) => {
      const hasTables = item?.tables && item?.tables?.length > 0;

      if (item?.children && item?.children?.length > 0) {
        item.children = this.removeTableLess(item.children);
        const childrenHaveTables = item.children.length > 0;
        return hasTables || childrenHaveTables;
      }

      return hasTables;
    });
  }
  assignLinks(node: any): any {
    node?.tables?.forEach((table: any) => {
      table.link = `/dynamic-table/${table.id}`;
    });
  }

  parseQueryString(queryString: string): any {
    const params = new URLSearchParams(queryString);
    const result: any = {};

    params.forEach((value, key) => {
      try {
        // Handle JSON values (e.g., arrays/objects)
        result[key] = value === 'null' ? null : JSON.parse(value);
      } catch {
        // Fallback for non-JSON values (e.g., numbers/strings)
        result[key] = isNaN(+value) ? value : +value;
      }
    });

    return result;
  }

  /**
   * Extract all table IDs from the menu items structure
   * and store them in CurrentUserService for access control
   */
  extractTableIds(items: any[]): void {
    const tableIds = new Set<number>();

    const extractFromItems = (itemsArray: any[]) => {
      itemsArray?.forEach((item) => {
        // Extract table IDs from current item
        if (item.tables && Array.isArray(item.tables)) {
          item.tables.forEach((table: any) => {
            if (table.id) {
              tableIds.add(table.id);
            }
          });
        }

        // Recursively process children if they exist
        if (item.children && Array.isArray(item.children)) {
          extractFromItems(item.children);
        }
      });
    };

    extractFromItems(items);
    this.currentUserService.allowedTableIds = tableIds;
  }
}
